/**
 * Support Center Page - Revolut-style Help Center
 *
 * Purpose: Canonical documentation hub for users, SEO, and LLM discovery
 * Design: Mobile-first, structured data, semantic HTML
 * Architecture: 8 categories, 50+ articles, search, popular articles
 * i18n: Full localization support for EN/RU/ES via react-i18next
 */

import { LandingFooter } from '@/components/public/LandingFooter';
import { PublicHeader } from '@/components/public/PublicHeader';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/EmptyState';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Display, Heading, Text } from '@/components/ui/typography';
import {
  getLocalizedArticles,
  getLocalizedArticleBySlug,
  getLocalizedArticlesByCategory,
  getLocalizedCategories,
  getLocalizedPopularArticles,
  searchLocalizedArticles,
  type SupportedLocale,
} from '@/content/help-center/locales';
import type { HelpArticle, HelpCategory } from '@/content/help-center';
import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import { Page } from '@supabank/ui';
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';
import { MessageCircle } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Get current locale from i18next
 */
function useLocale(): SupportedLocale {
  const { i18n } = useTranslation();
  const lang = i18n.language;
  // Map i18next language to our supported locales
  if (lang.startsWith('ru')) return 'ru';
  if (lang.startsWith('es')) return 'es';
  return 'en';
}

export default function SupportCenter() {
  const { t } = useTranslation('common');
  const locale = useLocale();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<HelpCategory | null>(null);
  const [selectedArticle, setSelectedArticle] = useState<HelpArticle | null>(null);

  // Get localized content
  const categories = useMemo(() => getLocalizedCategories(locale), [locale]);
  const allArticles = useMemo(() => getLocalizedArticles(locale), [locale]);

  // Search results
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    return searchLocalizedArticles(locale, searchQuery);
  }, [searchQuery, locale]);

  // Popular articles
  const popularArticles = useMemo(() => getLocalizedPopularArticles(locale), [locale]);

  // Category articles
  const categoryArticles = useMemo(() => {
    if (!selectedCategory) return [];
    return getLocalizedArticlesByCategory(locale, selectedCategory.id);
  }, [selectedCategory, locale]);

  // Handle article navigation from URL
  React.useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (hash) {
      const article = getLocalizedArticleBySlug(locale, hash);
      if (article) {
        setSelectedArticle(article);
        const category = categories.find(c => c.id === article.category);
        if (category) setSelectedCategory(category);
      }
    }
  }, [locale, categories]);

  // Update URL when article changes
  React.useEffect(() => {
    if (selectedArticle) {
      window.history.replaceState(null, '', `#${selectedArticle.slug}`);
    } else if (selectedCategory) {
      window.history.replaceState(null, '', `#${selectedCategory.id}`);
    } else {
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, [selectedArticle, selectedCategory]);

  const handleBack = () => {
    if (selectedArticle) {
      setSelectedArticle(null);
    } else if (selectedCategory) {
      setSelectedCategory(null);
    }
    setSearchQuery('');
  };

  const handleCategorySelect = (category: HelpCategory) => {
    setSelectedCategory(category);
    setSelectedArticle(null);
    setSearchQuery('');
  };

  const handleArticleSelect = (article: HelpArticle) => {
    setSelectedArticle(article);
    const category = categories.find(c => c.id === article.category);
    if (category) setSelectedCategory(category);
    setSearchQuery('');
  };

  const handleSearchSelect = (article: HelpArticle) => {
    handleArticleSelect(article);
  };

  // Render article content with markdown-like formatting
  const renderArticleContent = (content: string) => {
    const lines = content.split('\n');
    const elements: React.ReactNode[] = [];
    let inCodeBlock = false;
    let codeContent: string[] = [];
    let inTable = false;
    let tableRows: string[][] = [];

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      // Code blocks
      if (line.startsWith('```')) {
        if (inCodeBlock) {
          elements.push(
            <pre key={`code-${i}`} className="bg-muted p-4 rounded-lg overflow-x-auto my-4 text-sm">
              <code>{codeContent.join('\n')}</code>
            </pre>
          );
          codeContent = [];
          inCodeBlock = false;
        } else {
          inCodeBlock = true;
        }
        continue;
      }

      if (inCodeBlock) {
        codeContent.push(line);
        continue;
      }

      // Tables
      if (line.startsWith('|') && line.endsWith('|')) {
        if (!inTable) {
          inTable = true;
          tableRows = [];
        }
        // Skip separator row
        if (line.includes('---')) continue;

        const cells = line.split('|').filter(c => c.trim());
        tableRows.push(cells.map(c => c.trim()));
        continue;
      } else if (inTable) {
        // End of table
        elements.push(
          <div key={`table-${i}`} className="overflow-x-auto my-4">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b border-border">
                  {tableRows[0]?.map((cell, ci) => (
                    <th key={ci} className="text-left p-2 text-sm font-semibold">
                      {cell}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {tableRows.slice(1).map((row, ri) => (
                  <tr key={ri} className="border-b border-border/50">
                    {row.map((cell, ci) => (
                      <td key={ci} className="p-2 text-sm">
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
        tableRows = [];
        inTable = false;
      }

      // Skip empty lines
      if (!line.trim()) {
        continue;
      }

      // Headings
      if (line.startsWith('# ')) {
        elements.push(
          <Heading key={i} variant="h1" className="mt-8 mb-4 first:mt-0">
            {line.slice(2)}
          </Heading>
        );
        continue;
      }
      if (line.startsWith('## ')) {
        elements.push(
          <Heading key={i} variant="h2" className="mt-6 mb-3">
            {line.slice(3)}
          </Heading>
        );
        continue;
      }
      if (line.startsWith('### ')) {
        elements.push(
          <Heading key={i} variant="h3" className="mt-4 mb-2">
            {line.slice(4)}
          </Heading>
        );
        continue;
      }

      // Lists
      if (line.startsWith('- ') || line.startsWith('* ')) {
        const content = line.slice(2);
        elements.push(
          <div key={i} className="flex gap-2 ml-4 my-1">
            <span className="text-muted-foreground">•</span>
            <Text>{formatInlineText(content)}</Text>
          </div>
        );
        continue;
      }

      // Numbered lists
      if (/^\d+\.\s/.test(line)) {
        const match = line.match(/^(\d+)\.\s(.+)$/);
        if (match) {
          elements.push(
            <div key={i} className="flex gap-2 ml-4 my-1">
              <span className="text-muted-foreground font-medium">{match[1]}.</span>
              <Text>{formatInlineText(match[2])}</Text>
            </div>
          );
        }
        continue;
      }

      // Callout boxes
      if (line.startsWith('⚠️') || line.startsWith('✅') || line.startsWith('❌')) {
        elements.push(
          <div key={i} className="flex gap-2 my-2 p-3 bg-muted/50 rounded-lg">
            <Text>{formatInlineText(line)}</Text>
          </div>
        );
        continue;
      }

      // Regular paragraphs
      elements.push(
        <Text key={i} className="my-2">
          {formatInlineText(line)}
        </Text>
      );
    }

    return elements;
  };

  // Format inline text (bold, code, links)
  const formatInlineText = (text: string): React.ReactNode => {
    const parts: React.ReactNode[] = [];
    let remaining = text;
    let key = 0;

    while (remaining) {
      // Bold
      const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
      // Inline code
      const codeMatch = remaining.match(/`(.+?)`/);

      if (boldMatch && (!codeMatch || boldMatch.index! < codeMatch.index!)) {
        const idx = boldMatch.index!;
        if (idx > 0) parts.push(remaining.slice(0, idx));
        parts.push(<strong key={key++}>{boldMatch[1]}</strong>);
        remaining = remaining.slice(idx + boldMatch[0].length);
      } else if (codeMatch) {
        const idx = codeMatch.index!;
        if (idx > 0) parts.push(remaining.slice(0, idx));
        parts.push(
          <code key={key++} className="bg-muted px-1.5 py-0.5 rounded text-sm">
            {codeMatch[1]}
          </code>
        );
        remaining = remaining.slice(idx + codeMatch[0].length);
      } else {
        parts.push(remaining);
        break;
      }
    }

    return parts;
  };

  // Helper for pluralized article count
  const getArticleCountText = (count: number): string => {
    if (locale === 'ru') {
      // Russian pluralization
      const lastTwo = count % 100;
      const lastOne = count % 10;
      if (lastTwo >= 11 && lastTwo <= 19) {
        return `${count} статей`;
      }
      if (lastOne === 1) {
        return `${count} статья`;
      }
      if (lastOne >= 2 && lastOne <= 4) {
        return `${count} статьи`;
      }
      return `${count} статей`;
    }
    // English pluralization
    return count === 1 ? `${count} article` : `${count} articles`;
  };

  return (
    <Page data-ux-contract="canon" role="main" className="bg-background text-foreground min-h-screen">
      <PublicHeader />

      {/* Hero Section */}
      <section className="relative py-12 sm:py-16 md:py-20 px-4 sm:px-6 bg-gradient-to-b from-primary/5 to-background">
        <div className="max-w-4xl mx-auto text-center">
          {/* Breadcrumb */}
          {(selectedCategory || selectedArticle) && (
            <button
              onClick={handleBack}
              className={cn(
                "inline-flex items-center gap-2 mb-6",
                "text-primary hover:text-primary/80",
                semanticTokens.motion.fast
              )}
            >
              <SFIcon icon={sfIcons.back} className="h-4 w-4" />
              <Text variant="sm">
                {selectedArticle ? selectedCategory?.title : t('supportCenter.allCategories')}
              </Text>
            </button>
          )}

          <Display variant="lg" className="mb-4">
            {selectedArticle
              ? selectedArticle.title
              : selectedCategory
                ? selectedCategory.title
                : t('supportCenter.title')}
          </Display>

          {!selectedArticle && (
            <Text variant="lg" color="muted" className="mb-8 max-w-2xl mx-auto">
              {selectedCategory
                ? selectedCategory.description
                : t('supportCenter.subtitle')}
            </Text>
          )}

          {/* Search */}
          {!selectedArticle && (
            <div className="relative max-w-2xl mx-auto">
              <SFIcon icon={sfIcons.search} className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
              <Input
                type="search"
                placeholder={t('supportCenter.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-12 h-14 text-lg"
                aria-label={t('supportCenter.searchAriaLabel')}
              />

              {/* Search results dropdown */}
              {searchQuery && searchResults.length > 0 && (
                <Card className="absolute top-full left-0 right-0 mt-2 z-50 overflow-hidden">
                  <ScrollArea className="max-h-80">
                    {searchResults.slice(0, 8).map((article) => (
                      <button
                        key={article.id}
                        onClick={() => handleSearchSelect(article)}
                        className={cn(
                          "w-full text-left px-4 py-3",
                          "hover:bg-muted/50",
                          "border-b border-border/50 last:border-0",
                          semanticTokens.motion.fast
                        )}
                      >
                        <Text className="font-medium">{article.title}</Text>
                        <Text variant="sm" color="muted" className="line-clamp-1">
                          {article.description}
                        </Text>
                      </button>
                    ))}
                  </ScrollArea>
                </Card>
              )}

              {searchQuery && searchResults.length === 0 && (
                <Card className="absolute top-full left-0 right-0 mt-2 z-50 p-4">
                  <EmptyState
                    variant="no-results"
                    title={t('supportCenter.noResults.title')}
                    description={t('supportCenter.noResults.description', { query: searchQuery })}
                    cta={{
                      label: t('supportCenter.noResults.clearSearch'),
                      onClick: () => setSearchQuery(''),
                    }}
                  />
                </Card>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 sm:py-16 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">

          {/* Article Detail View */}
          {selectedArticle ? (
            <Card className="p-6 sm:p-8 lg:p-10">
              <div className="prose prose-slate dark:prose-invert max-w-none">
                {renderArticleContent(selectedArticle.content)}
              </div>

              {/* Article footer */}
              <div className="mt-8 pt-6 border-t border-border">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                  <Text variant="sm" color="muted">
                    {t('supportCenter.articleFooter.wasHelpful')}
                  </Text>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm">
                      👍 {t('supportCenter.articleFooter.yes')}
                    </Button>
                    <Button variant="outline" size="sm">
                      👎 {t('supportCenter.articleFooter.no')}
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          ) : selectedCategory ? (
            /* Category Articles View */
            categoryArticles.length === 0 ? (
              <EmptyState
                variant="no-data"
                title={t('supportCenter.noArticles.title', { category: selectedCategory.title })}
                description={t('supportCenter.noArticles.description')}
                cta={{
                  label: t('supportCenter.noArticles.browseAll'),
                  onClick: () => {
                    setSelectedCategory(null);
                    setSearchQuery('');
                  },
                }}
              />
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {categoryArticles.map((article) => (
                  <Card
                    key={article.id}
                    className={cn(
                      "p-6 cursor-pointer",
                      "hover:shadow-lg hover:border-primary/20",
                      semanticTokens.motion.standard
                    )}
                    onClick={() => handleArticleSelect(article)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && handleArticleSelect(article)}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <Heading variant="h4" className="mb-2">
                          {article.title}
                        </Heading>
                        <Text variant="sm" color="muted">
                          {article.description}
                        </Text>
                      </div>
                      <SFIcon icon={sfIcons.chevronRight} className="h-5 w-5 text-muted-foreground flex-shrink-0 mt-1" />
                    </div>
                  </Card>
                ))}
              </div>
            )
          ) : (
            /* Main Help Center View */
            <div className="space-y-12">

              {/* Popular Articles */}
              <div>
                <div className="flex items-center gap-2 mb-6">
                  <SFIcon icon={sfIcons.activity} className={cn("h-5 w-5", semanticTokens.warning.accent)} />
                  <Heading variant="h2">{t('supportCenter.popularArticles')}</Heading>
                </div>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {popularArticles.map((article) => (
                    <Card
                      key={article.id}
                      className={cn(
                        "p-5 cursor-pointer",
                        "hover:shadow-lg hover:border-primary/20",
                        semanticTokens.motion.standard
                      )}
                      onClick={() => handleArticleSelect(article)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => e.key === 'Enter' && handleArticleSelect(article)}
                    >
                      <Text className="font-medium mb-1 line-clamp-1">
                        {article.title}
                      </Text>
                      <Text variant="sm" color="muted" className="line-clamp-2">
                        {article.description}
                      </Text>
                    </Card>
                  ))}
                </div>
              </div>

              {/* Categories Grid */}
              <div>
                <Heading variant="h2" className="mb-6">{t('supportCenter.browseByCategory')}</Heading>
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  {categories.map((category) => {
                    const Icon = category.icon;
                    const articleCount = getLocalizedArticlesByCategory(locale, category.id).length;

                    return (
                      <Card
                        key={category.id}
                        className={cn(
                          "p-6 cursor-pointer text-center",
                          "hover:shadow-lg hover:border-primary/20",
                          semanticTokens.motion.standard
                        )}
                        onClick={() => handleCategorySelect(category)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === 'Enter' && handleCategorySelect(category)}
                      >
                        <div className={cn(
                          "w-12 h-12 mx-auto mb-4 rounded-xl",
                          "bg-primary/10 flex items-center justify-center"
                        )}>
                          <Icon className="h-6 w-6 text-primary" />
                        </div>
                        <Heading variant="h4" className="mb-1">
                          {category.title}
                        </Heading>
                        <Text variant="sm" color="muted">
                          {getArticleCountText(articleCount)}
                        </Text>
                      </Card>
                    );
                  })}
                </div>
              </div>

              {/* Still Need Help CTA */}
              <Card className={cn(
                "p-8 text-center",
                "bg-gradient-to-br from-primary/5 to-primary/10",
                "border-primary/20"
              )}>
                <div className={cn(
                  "w-16 h-16 mx-auto mb-4 rounded-full",
                  "bg-primary/20 flex items-center justify-center"
                )}>
                  <MessageCircle className="h-8 w-8 text-primary" />
                </div>
                <Heading variant="h3" className="mb-2">
                  {t('supportCenter.needHelp.title')}
                </Heading>
                <Text color="muted" className="mb-6 max-w-md mx-auto">
                  {t('supportCenter.needHelp.description')}
                </Text>
                <Button size="lg" className="gap-2">
                  <MessageCircle className="h-5 w-5" />
                  {t('supportCenter.needHelp.cta')}
                </Button>
              </Card>
            </div>
          )}
        </div>
      </section>

      {/* SEO-friendly structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            mainEntity: allArticles.slice(0, 20).map(article => ({
              '@type': 'Question',
              name: article.title,
              acceptedAnswer: {
                '@type': 'Answer',
                text: article.description,
              },
            })),
          }),
        }}
      />

      <LandingFooter />
    </Page>
  );
}
